// src/pages/admin/AdminProviderApplications.jsx

import React, {
  useState,
  useEffect,
  useCallback
} from 'react';

import { useDispatch, useSelector } from 'react-redux';

import {
  Search,
  CheckCircle,
  XCircle,
  Clock,
  Eye,
  Briefcase,
  MapPin,
  Star,
  FileText,
  RefreshCw,
  AlertCircle,
  Trash2
} from 'lucide-react';

import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

import styles from '../../styles/Application.module.css';
import useTranslation from '../../hooks/useTranslation';

import {
  adminGetAllProviders,
  adminGetProviderById,
  adminDeleteProvider
} from '../../store/slices/adminProviderSlice';

import {
  selectAdminAllProviders,
  selectAdminCurrentProvider,
  selectAdminPagination,
  selectAdminLoading,
  selectAdminDeleting,
  selectAdminIsRefreshing
} from '../../store/slices/adminProviderSlice';

const AdminProviderApplications = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // =========================================================
  // LOCAL STATE
  // =========================================================

  const [selectedApplication, setSelectedApplication] =
    useState(null);

  const [filter, setFilter] = useState('all');

  const [searchTerm, setSearchTerm] = useState('');

  const [showDetails, setShowDetails] = useState(false);

  const [page, setPage] = useState(1);

  const [limit] = useState(10);

  const [processingId, setProcessingId] = useState(null);

  // =========================================================
  // REDUX
  // =========================================================

  const providers = useSelector(selectAdminAllProviders);

  const currentProvider = useSelector(
    selectAdminCurrentProvider
  );

  const pagination = useSelector(
    selectAdminPagination
  );

  const loading = useSelector(
    selectAdminLoading
  );

  const deleting = useSelector(
    selectAdminDeleting
  );

  const isRefreshing = useSelector(
    selectAdminIsRefreshing
  );

  // =========================================================
  // TRANSLATIONS
  // =========================================================

  const {
    translatedText: providerApplications
  } = useTranslation('Provider Applications');

  const {
    translatedText: total
  } = useTranslation('Total');

  const {
    translatedText: refresh
  } = useTranslation('Refresh');

  const {
    translatedText: allApplications
  } = useTranslation('All Applications');

  const {
    translatedText: searchPlaceholder
  } = useTranslation('Search applications...');

  const {
    translatedText: noApplications
  } = useTranslation('No applications found');

  const {
    translatedText: noApplicationsDesc
  } = useTranslation(
    'There are no provider applications matching your criteria'
  );

  const {
    translatedText: view
  } = useTranslation('View');

  const {
    translatedText: applicationDetails
  } = useTranslation('Application Details');

  const {
    translatedText: personalInformation
  } = useTranslation('Personal Information');

  const {
    translatedText: businessInformation
  } = useTranslation('Business Information');

  const {
    translatedText: name
  } = useTranslation('Name');

  const {
    translatedText: email
  } = useTranslation('Email');

  const {
    translatedText: phone
  } = useTranslation('Phone');

  const {
    translatedText: serviceType
  } = useTranslation('Service Type');

  const {
    translatedText: businessName
  } = useTranslation('Business Name');

  const {
    translatedText: yearsExperience
  } = useTranslation('Years Experience');

  const {
    translatedText: location
  } = useTranslation('Location');

  const {
    translatedText: description
  } = useTranslation('Description');

  const {
    translatedText: noDescription
  } = useTranslation(
    'No description provided'
  );

  const {
    translatedText: nA
  } = useTranslation('N/A');

  const {
    translatedText: years
  } = useTranslation('years');

  const {
    translatedText: loadingApplications
  } = useTranslation(
    'Loading applications...'
  );

  const {
    translatedText: noBusinessName
  } = useTranslation(
    'No business name'
  );

  const {
    translatedText: deleteProvider
  } = useTranslation(
    'Delete Provider'
  );

  const {
    translatedText: confirmDelete
  } = useTranslation(
    'Are you sure you want to delete this provider?'
  );

  const {
    translatedText: deleteSuccess
  } = useTranslation(
    'Provider deleted successfully'
  );

  const {
    translatedText: deleteErrorMsg
  } = useTranslation(
    'Failed to delete provider'
  );

  const {
    translatedText: active
  } = useTranslation('Active');

  // =========================================================
  // FETCH APPLICATIONS
  // =========================================================

  const fetchApplications = useCallback(() => {
    const params = {
      page,
      limit,
      search: searchTerm.trim() || undefined
    };

    if (filter !== 'all') {
      params.status = filter;
    }

    dispatch(adminGetAllProviders(params));
  }, [dispatch, page, limit, searchTerm, filter]);

  // =========================================================
  // INITIAL / FILTER / PAGE FETCH
  // =========================================================

  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);

  // =========================================================
  // SEARCH DEBOUNCE
  // =========================================================

  useEffect(() => {
    const trimmedSearch = searchTerm.trim();

    const timer = setTimeout(() => {
      if (trimmedSearch && page !== 1) {
        setPage(1);
      }
    }, 500);

    return () => {
      clearTimeout(timer);
    };
  }, [searchTerm, page]);

  // =========================================================
  // DELETE PROVIDER
  // =========================================================

  const handleDelete = async (providerId) => {
    if (!providerId) {
      return;
    }

    const confirmed = window.confirm(
      confirmDelete ||
      'Are you sure you want to delete this provider?'
    );

    if (!confirmed) {
      return;
    }

    try {
      const result = await dispatch(
        adminDeleteProvider(providerId)
      ).unwrap();

      if (result?.success) {
        toast.success(
          deleteSuccess ||
          'Provider deleted successfully'
        );

        await fetchApplications();
      } else {
        toast.error(
          result?.message ||
          deleteErrorMsg ||
          'Failed to delete provider'
        );
      }
    } catch (error) {
      console.error(
        'Delete error:',
        error
      );

      toast.error(
        error?.message ||
        deleteErrorMsg ||
        'Failed to delete provider'
      );
    }
  };

  // =========================================================
  // VIEW DETAILS
  // =========================================================

  const viewDetails = async (provider) => {
    if (!provider?.id) {
      return;
    }

    setSelectedApplication(provider);
    setShowDetails(true);

    try {
      const result = await dispatch(
        adminGetProviderById({
          id: provider.id
        })
      ).unwrap();

      if (result?.success && result?.data) {
        setSelectedApplication(
          (previous) => ({
            ...previous,
            ...result.data
          })
        );
      }
    } catch (error) {
      console.error(
        'Failed to load provider details:',
        error
      );
    }
  };

  // =========================================================
  // STATUS BADGE
  // =========================================================

  const getStatusBadge = (provider) => {
    const isActive = provider?.isActive !== false;
    
    if (isActive) {
      return (
        <span className={styles.badgeApproved}>
          <CheckCircle size={14} />
          {active || 'Active'}
        </span>
      );
    }

    return (
      <span className={styles.badgeSuspended}>
        <AlertCircle size={14} />
        Inactive
      </span>
    );
  };

  // =========================================================
  // FILTER OPTIONS
  // =========================================================

  const filterOptions = [
    {
      value: 'all',
      label: allApplications || 'All Applications'
    },
    {
      value: 'active',
      label: active || 'Active'
    },
    {
      value: 'inactive',
      label: 'Inactive'
    }
  ];

  // =========================================================
  // FORMAT DATE
  // =========================================================

  const formatDate = (dateString) => {
    if (!dateString) {
      return 'N/A';
    }

    const date = new Date(dateString);

    if (Number.isNaN(date.getTime())) {
      return 'N/A';
    }

    return date.toLocaleDateString(
      'en-US',
      {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }
    );
  };

  // =========================================================
  // PROVIDER LIST
  // =========================================================

  const providersList = Array.isArray(providers) ? providers : [];

  // =========================================================
  // LOADING
  // =========================================================

  if (loading && !providersList.length) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.spinner} />
        <p>{loadingApplications || 'Loading applications...'}</p>
      </div>
    );
  }

  // =========================================================
  // RENDER
  // =========================================================

  return (
    <div className={styles.applicationsContainer}>

      {/* =====================================================
          HEADER
      ===================================================== */}

      <div className={styles.sectionHeader}>
        <div className={styles.headerLeft}>
          <h2>
            {providerApplications ||
              'Provider Applications'}
          </h2>

          <div className={styles.statsBadges}>
            <span className={styles.statBadge}>
              {total || 'Total'}: {providersList.length}
            </span>
          </div>
        </div>

        <button
          type="button"
          className={styles.refreshBtn}
          onClick={fetchApplications}
          disabled={isRefreshing || loading}
        >
          <RefreshCw
            size={18}
            className={isRefreshing ? styles.spinning : ''}
          />
          {refresh || 'Refresh'}
        </button>
      </div>

      {/* =====================================================
          FILTERS
      ===================================================== */}

      <div className={styles.filterSection}>
        <div className={styles.filterTabs}>
          {filterOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              className={`${styles.filterTab} ${
                filter === option.value ? styles.active : ''
              }`}
              onClick={() => {
                setPage(1);
                setFilter(option.value);
              }}
            >
              {option.label}
            </button>
          ))}
        </div>

        <div className={styles.searchBox}>
          <Search size={18} />
          <input
            type="text"
            placeholder={
              searchPlaceholder ||
              'Search applications...'
            }
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setPage(1);
              }
            }}
          />
        </div>
      </div>

      {/* =====================================================
          APPLICATIONS LIST
      ===================================================== */}

      <div className={styles.applicationsList}>
        {providersList.length === 0 ? (
          <div className={styles.emptyState}>
            <FileText size={48} />
            <h3>{noApplications || 'No applications found'}</h3>
            <p>
              {noApplicationsDesc ||
                'There are no provider applications matching your criteria'}
            </p>
          </div>
        ) : (
          providersList.map((provider) => {
            return (
              <div
                key={provider.id || provider._id}
                className={styles.applicationCard}
              >

                {/* =================================================
                    CARD HEADER
                ================================================= */}

                <div className={styles.cardHeader}>
                  <div className={styles.providerInfo}>

                    {/* AVATAR */}

                    <div className={styles.avatar}>
                      {provider.profileImage &&
                      provider.profileImage.url ? (
                        <img
                          src={provider.profileImage.url}
                          alt={provider.name || 'Provider'}
                          className={styles.avatarImage}
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                            const parent = e.currentTarget.parentElement;
                            if (parent) {
                              parent.innerHTML =
                                `<span>${provider.name?.charAt(0) || 'P'}</span>`;
                            }
                          }}
                        />
                      ) : (
                        <span>
                          {provider.name?.charAt(0) || 'P'}
                        </span>
                      )}
                    </div>

                    {/* PROVIDER INFORMATION */}

                    <div className={styles.info}>
                      <h4>{provider.name || 'Unknown Provider'}</h4>

                      <p className={styles.businessName}>
                        {provider.businessName ||
                          noBusinessName ||
                          'No business name'}
                      </p>

                      <div className={styles.metaTags}>
                        <span className={styles.metaTag}>
                          <Briefcase size={14} />
                          {provider.serviceType || nA || 'N/A'}
                        </span>

                        <span className={styles.metaTag}>
                          <MapPin size={14} />
                          {provider.address?.city || nA || 'N/A'}
                        </span>

                        <span className={styles.metaTag}>
                          <Star size={14} />
                          {provider.rating || 0} (
                          {provider.reviewCount || 0})
                        </span>

                        <span className={styles.metaTag}>
                          <Clock size={14} />
                          {formatDate(provider.createdAt)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* CARD ACTIONS */}

                  <div className={styles.cardActions}>
                    {getStatusBadge(provider)}

                    <button
                      type="button"
                      className={styles.viewBtn}
                      onClick={() => viewDetails(provider)}
                    >
                      <Eye size={18} />
                      {view || 'View'}
                    </button>

                    <button
                      type="button"
                      className={styles.deleteBtn}
                      onClick={() => handleDelete(provider.id)}
                      disabled={deleting}
                    >
                      <Trash2 size={18} />
                      {deleteProvider || 'Delete'}
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* =====================================================
          PAGINATION
      ===================================================== */}

      {pagination &&
        (pagination.pages || pagination.totalPages) > 1 && (
          <div className={styles.pagination}>
            <button
              type="button"
              className={styles.pageBtn}
              onClick={() =>
                setPage((previous) => Math.max(1, previous - 1))
              }
              disabled={page === 1 || loading}
            >
              Previous
            </button>

            <span className={styles.pageInfo}>
              Page {pagination.page || page} of{' '}
              {pagination.pages || pagination.totalPages}
            </span>

            <button
              type="button"
              className={styles.pageBtn}
              onClick={() => setPage((previous) => previous + 1)}
              disabled={
                page >= (pagination.pages || pagination.totalPages) ||
                loading
              }
            >
              Next
            </button>
          </div>
        )}

      {/* =====================================================
          DETAILS MODAL
      ===================================================== */}

      {showDetails && selectedApplication && (
        <div
          className={styles.modalOverlay}
          onClick={() => setShowDetails(false)}
        >
          <div
            className={styles.modalContent}
            onClick={(e) => e.stopPropagation()}
          >

            {/* MODAL HEADER */}

            <div className={styles.modalHeader}>
              <h3>
                {applicationDetails ||
                  'Application Details'}
              </h3>

              <button
                type="button"
                className={styles.closeBtn}
                onClick={() => setShowDetails(false)}
              >
                <XCircle size={24} />
              </button>
            </div>

            {/* MODAL BODY */}

            <div className={styles.modalBody}>

              {/* PERSONAL INFORMATION */}

              <div className={styles.detailSection}>
                <h4>
                  {personalInformation ||
                    'Personal Information'}
                </h4>

                <div className={styles.detailGrid}>
                  <div className={styles.detailItem}>
                    <label>{name || 'Name'}</label>
                    <p>{selectedApplication.name || 'N/A'}</p>
                  </div>

                  <div className={styles.detailItem}>
                    <label>{email || 'Email'}</label>
                    <p>{selectedApplication.email || 'N/A'}</p>
                  </div>

                  <div className={styles.detailItem}>
                    <label>{phone || 'Phone'}</label>
                    <p>{selectedApplication.phone || 'N/A'}</p>
                  </div>

                  <div className={styles.detailItem}>
                    <label>{serviceType || 'Service Type'}</label>
                    <p>{selectedApplication.serviceType || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* BUSINESS INFORMATION */}

              <div className={styles.detailSection}>
                <h4>
                  {businessInformation ||
                    'Business Information'}
                </h4>

                <div className={styles.detailGrid}>
                  <div className={styles.detailItem}>
                    <label>{businessName || 'Business Name'}</label>
                    <p>
                      {selectedApplication.businessName ||
                        noBusinessName ||
                        'No business name'}
                    </p>
                  </div>

                  <div className={styles.detailItem}>
                    <label>{yearsExperience || 'Years Experience'}</label>
                    <p>
                      {selectedApplication.yearsOfExperience || 0}{' '}
                      {years || 'years'}
                    </p>
                  </div>

                  <div className={styles.detailItem}>
                    <label>{location || 'Location'}</label>
                    <p>
                      {selectedApplication.address?.city || 'N/A'}
                      , {selectedApplication.address?.state || ''}
                      {' '}
                      {selectedApplication.address?.zipCode || ''}
                      <br />
                      {selectedApplication.address?.country || ''}
                    </p>
                  </div>

                  {/* STATUS */}

                  <div className={styles.detailItem}>
                    <label>Status</label>
                    <p>{getStatusBadge(selectedApplication)}</p>
                  </div>

                  {/* RATING */}

                  <div className={styles.detailItem}>
                    <label>Rating</label>
                    <p>
                      {selectedApplication.rating || 0} (
                      {selectedApplication.reviewCount || 0} reviews)
                    </p>
                  </div>

                  {/* FEATURED */}

                  <div className={styles.detailItem}>
                    <label>Featured</label>
                    <p>
                      {selectedApplication.isFeatured ? 'Yes' : 'No'}
                    </p>
                  </div>
                </div>
              </div>

              {/* DESCRIPTION */}

              <div className={styles.detailSection}>
                <h4>{description || 'Description'}</h4>
                <p className={styles.description}>
                  {selectedApplication.description ||
                    noDescription ||
                    'No description provided'}
                </p>
              </div>

              {/* LANGUAGES */}

              {selectedApplication.languages &&
                selectedApplication.languages.length > 0 && (
                  <div className={styles.detailSection}>
                    <h4>Languages</h4>
                    <div className={styles.languageTags}>
                      {selectedApplication.languages.map(
                        (lang, index) => (
                          <span
                            key={`${lang}-${index}`}
                            className={styles.languageTag}
                          >
                            {lang}
                          </span>
                        )
                      )}
                    </div>
                  </div>
                )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminProviderApplications;